import { trackMixpanelEvent } from './analytics';
import { AnalyticsEvents, type EventPropertiesMap } from './events';
import { supabase } from '../services/supabase/client';

/** Fire North Star + tail payment when a full transaction closes */
export function trackPaymentFulfilled(params: {
  requestId: string;
  transactionId?: string;
  isTailFlow: boolean;
  paidAmount?: number;
  gmv?: number;
  categoryId?: number;
  pricingMode?: string;
}): void {
  const { requestId, transactionId, isTailFlow, paidAmount, gmv, categoryId, pricingMode } =
    params;

  if (isTailFlow) {
    trackMixpanelEvent(AnalyticsEvents.TAIL_PAYMENT_SUCCEEDED, {
      request_id: requestId,
      amount: paidAmount,
      transaction_id: transactionId,
    });
    trackMixpanelEvent(AnalyticsEvents.SERVICE_COMPLETED, {
      request_id: requestId,
      gmv,
      category_id: categoryId,
      pricing_mode: pricingMode,
    });
    return;
  }

  trackMixpanelEvent(AnalyticsEvents.DEPOSIT_PAYMENT_SUCCEEDED, {
    request_id: requestId,
    amount: paidAmount,
    transaction_id: transactionId,
  });
}

export function trackDepositPaymentFailed(
  props: EventPropertiesMap['deposit_payment_failed'],
): void {
  trackMixpanelEvent(AnalyticsEvents.DEPOSIT_PAYMENT_FAILED, props);
}

export const MarketplaceAnalytics = {
  searchPerformed: (props: EventPropertiesMap['search_performed']) =>
    trackMixpanelEvent(AnalyticsEvents.SEARCH_PERFORMED, props),

  craftsmanProfileViewed: (props: EventPropertiesMap['craftsman_profile_viewed']) =>
    trackMixpanelEvent(AnalyticsEvents.CRAFTSMAN_PROFILE_VIEWED, props),

  serviceRequestCreated: (props: EventPropertiesMap['service_request_created']) =>
    trackMixpanelEvent(AnalyticsEvents.SERVICE_REQUEST_CREATED, props),

  depositPaymentInitiated: (props: EventPropertiesMap['deposit_payment_initiated']) =>
    trackMixpanelEvent(AnalyticsEvents.DEPOSIT_PAYMENT_INITIATED, props),

  tailPaymentInitiated: (props: EventPropertiesMap['tail_payment_initiated']) =>
    trackMixpanelEvent(AnalyticsEvents.TAIL_PAYMENT_INITIATED, props),

  jobAccepted: (props: EventPropertiesMap['job_accepted']) =>
    trackMixpanelEvent(AnalyticsEvents.JOB_ACCEPTED, props),

  jobDeclined: (props: EventPropertiesMap['job_declined']) =>
    trackMixpanelEvent(AnalyticsEvents.JOB_DECLINED, props),

  serviceStatusChanged: (props: EventPropertiesMap['service_status_changed']) =>
    trackMixpanelEvent(AnalyticsEvents.SERVICE_STATUS_CHANGED, props),

  serviceHoursLogged: (props: EventPropertiesMap['service_hours_logged']) =>
    trackMixpanelEvent(AnalyticsEvents.SERVICE_HOURS_LOGGED, props),

  reviewSubmitted: (props: EventPropertiesMap['review_submitted']) =>
    trackMixpanelEvent(AnalyticsEvents.REVIEW_SUBMITTED, props),

  paymentFulfilled: trackPaymentFulfilled,
  depositPaymentFailed: trackDepositPaymentFailed,
};

/** Resolve service request metadata and fire payment funnel events */
export async function trackPaymentFulfilledFromPaymentRow(params: {
  requestId: string;
  transactionId?: string;
  isTailFlow: boolean;
  paidAmount?: number;
}): Promise<void> {
  const { requestId, transactionId, isTailFlow, paidAmount } = params;

  let gmv: number | undefined;
  let categoryId: number | undefined;
  let pricingMode: string | undefined;

  try {
    const { data: sr } = await supabase
      .from('service_requests')
      .select('actual_price, estimated_price, fixed_total_price, category_id, pricing_mode')
      .eq('id', requestId)
      .maybeSingle();

    if (sr) {
      categoryId = sr.category_id ?? undefined;
      pricingMode = sr.pricing_mode ?? undefined;
      gmv =
        Number(sr.actual_price) ||
        Number(sr.fixed_total_price) ||
        Number(sr.estimated_price) ||
        undefined;
    }
  } catch {
    // analytics must not block navigation
  }

  trackPaymentFulfilled({
    requestId,
    transactionId,
    isTailFlow,
    paidAmount,
    gmv,
    categoryId,
    pricingMode,
  });
}
